import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Dropdown } from 'semantic-ui-react';

// Components
import ProductDropdown from 'components/PageTitleAndFilters/components/ProductDropdown';
import DateRangePicker from 'components/PageTitleAndFilters/components/DateRangePicker';
import EquipmentSetDropdown from 'components/PageTitleAndFilters/components/EquipmentSetDropdown';
import ApplicationRateDropdown from 'components/PageTitleAndFilters/components/ApplicationRateDropdown';

import {
    PRODUCT_FILTER_TYPE,
    DATE_RANGE_FILTER_TYPE,
    EQUIPMENT_FILTER_TYPE,
    APPLICATION_RATE_FILTER_TYPE,
    ENTITIES_FILTER_TYPE,
    INNER_ENTITIES_FILTER_TYPE,
    SPRAY_EVENTS_NAMESPACE
} from 'services/sprayEvents/constants';

import { SprayEventFilterType } from 'services/sprayEvents/types';

import './styles.scss';
import EntityDropdownSearch from 'components/PageTitleAndFilters/components/EntityDropdownSearch';

import { AppState } from 'services/types';
import { FertilizerEventFilterType } from 'services/fertilizerEvents/types';
import {
    FERTILIZER_DATE_RANGE_FILTER_TYPE,
    FERTILIZER_ENTITIES_FILTER_TYPE,
    FERTILIZER_INNER_ENTITIES_FILTER_TYPE,
    FERTILIZER_PRODUCT_FILTER_TYPE
} from 'services/fertilizerEvents/constants';
import {
    searchTypeOptions,
    selectEntityTypeId,
    selectInnerEntityTypeId
} from './selectors';
import { selectFilterType } from '../EventsCurrentFilter/selectors';
import FertilizerProductDropdown from '../FertilizerProductDropdown';

export interface Props {
    namespace?: string;
}

const EventsSearch = (props: Props): React.ReactElement => {
    const filterType = useSelector((state: AppState) =>
        selectFilterType(state, props)
    );
    const typeOptions = useSelector((state: AppState) =>
        searchTypeOptions(state, props)
    );

    const getProductFilterType = () => {
        const { namespace } = props;
        return namespace === SPRAY_EVENTS_NAMESPACE
            ? PRODUCT_FILTER_TYPE
            : FERTILIZER_PRODUCT_FILTER_TYPE;
    };

    const defaultFilterType = typeOptions
        .map(({ value }) => value)
        .includes(filterType)
        ? filterType
        : getProductFilterType();

    const [type, setType] = useState<
        SprayEventFilterType | FertilizerEventFilterType
    >(defaultFilterType);
    const innerEntityTypeId = useSelector((state: AppState) =>
        selectInnerEntityTypeId(state, props)
    );
    const entityTypeId = useSelector((state: AppState) =>
        selectEntityTypeId(state, props)
    );

    const handleChange = (event: unknown, data: any) => {
        setType(data.value);
    };

    let filterComponent: React.ReactElement | null = null;

    switch (type) {
        case PRODUCT_FILTER_TYPE:
            filterComponent = (
                <ProductDropdown clearable={false} singleFilter />
            );
            break;
        case FERTILIZER_PRODUCT_FILTER_TYPE:
            filterComponent = (
                <FertilizerProductDropdown clearable={false} singleFilter />
            );
            break;
        case DATE_RANGE_FILTER_TYPE:
        case FERTILIZER_DATE_RANGE_FILTER_TYPE:
            filterComponent = <DateRangePicker singleFilter />;
            break;
        case EQUIPMENT_FILTER_TYPE:
            filterComponent = (
                <EquipmentSetDropdown clearable={false} singleFilter />
            );
            break;
        case APPLICATION_RATE_FILTER_TYPE:
            filterComponent = (
                <ApplicationRateDropdown clearable={false} singleFilter />
            );
            break;
        case ENTITIES_FILTER_TYPE:
        case FERTILIZER_ENTITIES_FILTER_TYPE:
            filterComponent = (
                <EntityDropdownSearch
                    entityTypeId={entityTypeId as number}
                    clearPageOnChange
                    showDeletedEntities
                    clearable={false}
                    singleFilter
                />
            );
            break;
        case INNER_ENTITIES_FILTER_TYPE:
        case FERTILIZER_INNER_ENTITIES_FILTER_TYPE:
            filterComponent = (
                <EntityDropdownSearch
                    isInnerEntity
                    entityTypeId={innerEntityTypeId as number}
                    clearPageOnChange
                    showDeletedEntities
                    clearable={false}
                    singleFilter
                />
            );
            break;
        default:
            break;
    }

    return (
        <div className="events-search ui form">
            <div className="search-type">
                <Dropdown
                    selection
                    value={type}
                    selectOnBlur={false}
                    options={typeOptions}
                    onChange={handleChange}
                    lazyLoad
                    closeOnChange
                    fluid
                />
            </div>
            <div className="search-value">{filterComponent}</div>
        </div>
    );
};

EventsSearch.defaultProps = {
    namespace: SPRAY_EVENTS_NAMESPACE
};
export default EventsSearch;
